import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { GripVertical, ChevronDown, Hash, Filter as FilterIcon } from "lucide-react";

import EventsSelectorMenu from "./EventsSelectorMenu";
import { getFixedPanelPositionFromRect } from "./eventSelectorGeometry";
import Icon from "shared/components/Icon";
import EventPropertyFilterModule from "features/activity/components/EventPropertyFilterModule";
import PropertyPickerPopover from "shared/components/filters/PropertyPickerPopover";
import { defaultOperatorForType } from "shared/components/filters/FilterValueEditor";
import { EventPropertyFilterChip } from "features/activity/components/EventsFilterModule";
import axiosInstance from "shared/services/api/axiosInstance";

const WINDOW_UNIT_OPTIONS = [
  { value: "seconds", label: "seconds" },
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
  { value: "session", label: "session" },
];

const MEASURING_OPTIONS = [
  { value: "conversion_rate", label: "Conversion rate" },
  { value: "unique_users", label: "Unique users" },
  { value: "total_conversions", label: "Total conversions" },
  { value: "total_sessions", label: "Total sessions" },
  { value: "time_to_convert", label: "Time to convert" },
];

const TTC_AGG_OPTIONS = [
  { value: "average", label: "Average" },
  { value: "median", label: "Median" },
  { value: "percentile", label: "Percentile" },
  { value: "minimum", label: "Minimum" },
  { value: "maximum", label: "Maximum" },
];

const pickerItemClass =
  "block w-full truncate px-2.5 py-1.5 text-left text-[11px] transition-colors rounded-sm";
const pickerMenuClass =
  "absolute left-0 top-full z-[120] mt-0.5 inline-block max-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-lg border py-1 shadow-lg";

/**
 * Query config for Funnel: ordered list of steps (each step = one event).
 * Reuses same Filter and Breakdown as Insight via parent RightPanel.
 */
export default function FunnelQueryConfig({
  funnelSteps = [],
  onFunnelStepsChange,
  availableEvents = [],
  teamId = null,
  funnelMeasuring = "conversion_rate",
  onFunnelMeasuringChange = null,
  funnelMeasuringProperty = null,
  onFunnelMeasuringPropertyChange = null,
  funnelMeasuringAggregation = "average",
  onFunnelMeasuringAggregationChange = null,
  funnelMeasuringPercentileValue = 50,
  onFunnelMeasuringPercentileValueChange = null,
  funnelConversionWindow = { value: 7, unit: 'days' },
  onFunnelConversionWindowChange = null,
  funnelAnyOrder = false,
  onFunnelAnyOrderChange = null,
  funnelCountSubject = "user",
  onFunnelCountSubjectChange = null,
  funnelAccumulationGroupKey = null,
  onFunnelAccumulationGroupKeyChange = null,
}) {
  const [stepMenuOpen, setStepMenuOpen] = useState(null); // 'add' | number (index to replace)
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const menuRef = useRef(null);
  const addButtonRef = useRef(null);
  
  // Filter popup state
  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false);
  const [selectedStepForFilter, setSelectedStepForFilter] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [editingPropertyFilter, setEditingPropertyFilter] = useState(null);
  const filterButtonRefs = useRef({});
  const propertyFilterButtonRefs = useRef({});
  const propertySumMigratedRef = useRef(false);

  useEffect(() => {
    if (propertySumMigratedRef.current) return;
    if (funnelMeasuring !== "property_sum" || !onFunnelMeasuringChange) return;
    propertySumMigratedRef.current = true;
    onFunnelMeasuringChange("conversion_rate");
    if (onFunnelMeasuringPropertyChange) onFunnelMeasuringPropertyChange(null);
  }, [funnelMeasuring, onFunnelMeasuringChange, onFunnelMeasuringPropertyChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && addButtonRef.current && !addButtonRef.current.contains(e.target)) {
        setStepMenuOpen(null);
        setMenuAnchorRect(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [stepMenuOpen]);

  const handleAddStep = (eventName, kindOrSource = "event", opts = {}) => {
    const id = `step-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const entityKind = (kindOrSource === 'agent' || kindOrSource === 'span') ? kindOrSource : 'event';
    const source = entityKind === 'event' ? (kindOrSource || 'event') : 'event';
    const spanKind = entityKind === 'span' ? (opts.spanKind || null) : null;
    const newStep = {
      id,
      eventName: eventName || "",
      entityName: eventName || "",
      label: eventName || "",
      source,
      entityKind,
      spanKind,
      filters: [],
    };
    onFunnelStepsChange([...funnelSteps, newStep]);
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleReplaceStep = (index, eventName, kindOrSource = "event", opts = {}) => {
    const entityKind = (kindOrSource === 'agent' || kindOrSource === 'span') ? kindOrSource : 'event';
    const source = entityKind === 'event' ? (kindOrSource || 'event') : 'event';
    const spanKind = entityKind === 'span' ? (opts.spanKind || null) : null;
    handleUpdateStep(index, {
      eventName: eventName || "",
      entityName: eventName || "",
      label: eventName || "",
      source, entityKind, spanKind,
    });
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleRemoveStep = (index) => {
    const next = funnelSteps.filter((_, i) => i !== index);
    onFunnelStepsChange(next);
  };

  const handleMoveStep = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= funnelSteps.length) return;
    const next = [...funnelSteps];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    onFunnelStepsChange(next);
  };

  const handleUpdateStep = (index, updates) => {
    const next = funnelSteps.map((s, i) => (i === index ? { ...s, ...updates } : s));
    onFunnelStepsChange(next);
  };

  const handleFilterClick = (step, stepIndex) => {
    if (!step.eventName && !step.entityName) return; // Can't add filters without an entity
    const source = step.source || 'event';
    setSelectedStepForFilter({
      id: `${step.eventName || step.entityName}\0${source}`,
      eventName: step.eventName || step.entityName,
      eventType: 'custom',
      source,
      entityKind: step.entityKind || 'event',
      spanKind: step.spanKind || null,
    });
    setSelectedStepIndex(stepIndex);
    setIsPropertyFilterOpen(true);
  };

  const handleFiltersChange = (filter) => {
    if (selectedStepIndex !== null && selectedStepForFilter) {
      const currentStep = funnelSteps[selectedStepIndex];
      const currentFilters = currentStep.filters || [];
      
      // Convert filter from EventPropertyFilterModule format to insights format
      const insightsFilter = {
        field: filter.property || filter.field,
        property: filter.property || filter.field,
        operator: filter.operator,
        value: filter.value,
        value2: filter.value2,
        propertyType: filter.propertyType,
        propertySource: filter.propertySource,
        propertyLabel: filter.propertyLabel || filter.property || filter.field,
        category: 'event_properties',
        eventName: selectedStepForFilter.eventName,
        listMode: filter.listMode,
        listItemType: filter.listItemType,
        sampleValues: filter.sampleValues || [],
        id: filter.id || `filter-${Date.now()}-${Math.random()}`,
        connector: currentFilters.length === 0 ? 'WHERE' : 'AND'
      };
      
      // If editing existing filter
      if (editingPropertyFilter) {
        const updatedFilters = currentFilters.map(f => 
          f.id === editingPropertyFilter.id ? insightsFilter : f
        );
        handleUpdateStep(selectedStepIndex, { filters: updatedFilters });
        setEditingPropertyFilter(null);
      } else {
        // Add new filter
        handleUpdateStep(selectedStepIndex, { filters: [...currentFilters, insightsFilter] });
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedStepForFilter(null);
      setSelectedStepIndex(null);
    }
  };

  const handleFirstTimeChange = (enabled, eventName, eventId) => {
    if (selectedStepIndex !== null && selectedStepForFilter) {
      const currentStep = funnelSteps[selectedStepIndex];
      const currentFilters = currentStep.filters || [];
      
      if (enabled) {
        // Remove any existing first_time filter first
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        // Add the first time filter
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals',
          value: true,
          category: 'first_time',
          eventName: selectedStepForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
        };
        handleUpdateStep(selectedStepIndex, { filters: [...withoutFirstTime, firstTimeFilter] });
      } else {
        // Remove the first time filter
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        handleUpdateStep(selectedStepIndex, { filters: withoutFirstTime });
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedStepForFilter(null);
      setSelectedStepIndex(null);
    }
  };

  const eventsList = Array.isArray(availableEvents) ? availableEvents : [];

  const cwRaw = funnelConversionWindow && typeof funnelConversionWindow === "object" ? funnelConversionWindow : {};
  let cwValue = parseInt(String(cwRaw.value ?? 7), 10);
  if (!Number.isFinite(cwValue) || cwValue < 1) cwValue = 7;
  const cwUnit = typeof cwRaw.unit === "string" && cwRaw.unit ? cwRaw.unit : "days";
  const conversionWindowResolved = { value: cwValue, unit: cwUnit };

  const hasTransactionStep = funnelSteps.some(
    (s) => s.source === "transaction" || s.eventType === "transaction"
  );

  const setConversionWindow = (patch) => {
    if (!onFunnelConversionWindowChange) return;
    onFunnelConversionWindowChange({
      ...conversionWindowResolved,
      ...patch,
    });
  };

  const [groupKeyOptions, setGroupKeyOptions] = useState([]);
  const [funnelMenuOpen, setFunnelMenuOpen] = useState(null); // 'unit' | 'subject' | 'measuring' | 'aggregation' | null
  const funnelControlsRef = useRef(null);

  useEffect(() => {
    const raw =
      teamId != null && teamId !== ""
        ? typeof teamId === "object" && teamId !== null
          ? teamId._id ?? teamId.id
          : teamId
        : null;
    if (raw == null || raw === "") {
      setGroupKeyOptions([]);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get("/group-identities/keys", { params: { team_id: raw } })
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setGroupKeyOptions(Array.isArray(res.data.keys) ? res.data.keys : []);
      })
      .catch(() => {
        if (!cancelled) setGroupKeyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const countSubjects = useMemo(() => {
    const base = [{ id: "user", label: "User", groupKey: null }];
    const fromApi = (groupKeyOptions || []).map((k) => ({
      id: `group:${k.group_key}`,
      label: k.group_key,
      groupKey: k.group_key,
    }));
    return [...base, ...fromApi];
  }, [groupKeyOptions]);

  useEffect(() => {
    const close = (e) => {
      if (!funnelMenuOpen) return;
      if (funnelControlsRef.current?.contains(e.target)) return;
      setFunnelMenuOpen(null);
    };
    const esc = (e) => {
      if (e.key === "Escape") setFunnelMenuOpen(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [funnelMenuOpen]);

  const measuringResolved =
    funnelMeasuring === "property_sum" ? "conversion_rate" : funnelMeasuring || "conversion_rate";

  const subjectDisplayLabel =
    funnelCountSubject === "group" && funnelAccumulationGroupKey
      ? `Group · ${funnelAccumulationGroupKey}`
      : "User";

  const isSubjectRowActive = (s) => {
    if (s.groupKey) {
      return funnelCountSubject === "group" && funnelAccumulationGroupKey === s.groupKey;
    }
    return (funnelCountSubject || "user") === "user";
  };

  const handleCountSubjectPick = (row) => {
    if (!onFunnelCountSubjectChange) return;
    if (row.groupKey) {
      onFunnelCountSubjectChange("group");
      onFunnelAccumulationGroupKeyChange?.(row.groupKey);
    } else {
      onFunnelCountSubjectChange("user");
      onFunnelAccumulationGroupKeyChange?.(null);
    }
    setFunnelMenuOpen(null);
  };

  const measuringLabel =
    MEASURING_OPTIONS.find((o) => o.value === measuringResolved)?.label || "Conversion rate";

  const ttcAggLabel =
    TTC_AGG_OPTIONS.find((o) => o.value === (funnelMeasuringAggregation || "average"))?.label || "Average";

  return (
    <>
      <div className="mb-2">
        <span className="text-sm font-medium text-ink-muted">Funnel steps</span>
      </div>

      {funnelSteps.length > 0 ? (
        <div
          className="mb-4 space-y-1 rounded-lg border p-2"
          style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-base)" }}
        >
          {funnelSteps.map((step, index) => {
            const stepFilters = step.filters || [];
            const hasFilters = stepFilters.length > 0;
            const isLast = index === funnelSteps.length - 1;
            return (
              <div key={step.id || index} className="space-y-1">
                <div
                  className="group/steprow relative flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs transition-colors hover:bg-surface-inverse/[0.04]"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", index.toString());
                    e.currentTarget.style.opacity = "0.5";
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                    if (dragIndex !== index) handleMoveStep(dragIndex, index);
                  }}
                >
                  <div className="flex-shrink-0 cursor-move text-ink-disabled hover:text-ink-secondary">
                    <GripVertical className="h-3.5 w-3.5" />
                  </div>
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dotted text-[11px] font-semibold tabular-nums"
                    style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
                    aria-hidden
                  >
                    {index + 1}
                  </div>
                  <span
                    className="min-w-0 flex-1 cursor-pointer truncate text-[12px] font-medium leading-snug"
                    style={{ color: "var(--text-secondary)" }}
                    onClick={(e) => {
                      if (stepMenuOpen === index) {
                        setStepMenuOpen(null);
                        setMenuAnchorRect(null);
                      } else {
                        setMenuAnchorRect(getFixedPanelPositionFromRect(e.currentTarget.getBoundingClientRect()));
                        setStepMenuOpen(index);
                      }
                    }}
                    title={step.eventName ? "Change event" : "Select event"}
                  >
                    {step.label || step.eventName || "Select event"}
                  </span>
                  {!isLast && (
                    <span className="shrink-0 text-[10px] font-medium" style={{ color: "var(--text-muted)" }}>
                      then
                    </span>
                  )}
                  <div className="flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/steprow:opacity-100 group-focus-within/steprow:opacity-100">
                    {step.eventName && (
                      <button
                        ref={(el) => { filterButtonRefs.current[`step-${index}`] = el; }}
                        type="button"
                        onClick={() => handleFilterClick(step, index)}
                        className="rounded p-0.5 hover:opacity-70"
                        title="Filter"
                      >
                        <FilterIcon size={14} color={hasFilters ? "var(--brand-primary)" : "var(--text-muted)"} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveStep(index)}
                      className="rounded p-0.5 hover:opacity-70"
                      title="Remove step"
                    >
                      <Icon name="delete" size={14} color="var(--text-muted)" />
                    </button>
                  </div>
                </div>
                
                {/* Display filter chips below each step */}
                {hasFilters && (
                  <div className="ml-11 space-y-1">
                    {stepFilters.map((filter, filterIndex) => {
                      const connector = filter.connector || (filterIndex === 0 ? 'WHERE' : 'AND');
                      
                      // Special handling for first_time filter
                      if (filter.category === 'first_time') {
                        return (
                          <div key={filter.id} className="flex flex-wrap items-center gap-1.5">
                            <span className="px-0.5 text-[10px] font-medium uppercase text-ink-muted">{connector}</span>
                            <div className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface-hover px-2 py-1">
                              <span className="text-[11px] font-medium text-ink">First time only</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedFilters = stepFilters.filter(f => f.id !== filter.id);
                                  handleUpdateStep(index, { filters: updatedFilters });
                                }}
                                className="rounded p-0.5 text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
                                title="Remove First time only"
                              >
                                <Icon name="delete" size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      }
                      
                      // Create ref object if it doesn't exist
                      if (!propertyFilterButtonRefs.current[filter.id]) {
                        propertyFilterButtonRefs.current[filter.id] = React.createRef();
                      }
                      
                      const onRemoveFilter = () => {
                        const updatedFilters = stepFilters
                          .filter(f => f.id !== filter.id)
                          .map((f, idx) => ({
                            ...f,
                            connector: idx === 0 ? 'WHERE' : (f.connector || 'AND')
                          }));
                        handleUpdateStep(index, { filters: updatedFilters });
                      };
                      
                      return (
                        <EventPropertyFilterChip
                          key={filter.id}
                          filter={filter}
                          compact
                          isFirst={filterIndex === 0}
                          filterRef={propertyFilterButtonRefs.current[filter.id]}
                          onEdit={() => {
                            setEditingPropertyFilter(filter);
                            setSelectedStepForFilter({
                              id: step.eventName || step.entityName,
                              eventName: step.eventName || step.entityName,
                              eventType: 'custom',
                              entityKind: step.entityKind || 'event',
                              spanKind: step.spanKind || null,
                            });
                            setSelectedStepIndex(index);
                            setIsPropertyFilterOpen(true);
                          }}
                          onUpdate={(updates) => {
                            const updatedFilters = stepFilters.map(f =>
                              f.id === filter.id ? { ...f, ...updates } : f
                            );
                            handleUpdateStep(index, { filters: updatedFilters });
                          }}
                          onRemove={onRemoveFilter}
                          teamId={teamId}
                          eventName={step.eventName}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {menuAnchorRect && stepMenuOpen !== null &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuAnchorRect.top,
              left: menuAnchorRect.left,
              width: menuAnchorRect.width,
              maxHeight: menuAnchorRect.maxHeightPx ?? 480,
              zIndex: 10000,
            }}
            className="flex max-h-[min(65vh,calc(100vh-24px))] flex-col overflow-hidden"
          >
            <EventsSelectorMenu
              availableEvents={eventsList}
              onSelect={(name, kind, opts) =>
                typeof stepMenuOpen === "number"
                  ? handleReplaceStep(stepMenuOpen, name, kind, opts)
                  : handleAddStep(name, kind, opts)
              }
              onClose={() => {
                setStepMenuOpen(null);
                setMenuAnchorRect(null);
              }}
              maxHeight={menuAnchorRect.maxHeightPx ?? 480}
            />
          </div>,
          document.body
        )}

      {/* Add step */}
      <div className="relative mt-1">
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => {
            if (stepMenuOpen === "add") {
              setStepMenuOpen(null);
              setMenuAnchorRect(null);
            } else {
              const el = addButtonRef.current;
              if (el) setMenuAnchorRect(getFixedPanelPositionFromRect(el.getBoundingClientRect()));
              setStepMenuOpen("add");
            }
          }}
          className="flex w-full items-center gap-2 rounded-lg border border-dashed px-2.5 py-1.5 text-left text-xs transition-colors"
          style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
        >
          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center font-medium text-ink-muted">
            +
          </span>
          <span className="min-w-0 flex-1">Add step</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
        </button>
      </div>

      {/* Window + measuring: inline copy (like insight metrics), not boxed selects */}
      <div ref={funnelControlsRef} className="mt-4 space-y-3">
        <p
          className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12px] leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <span className="flex flex-wrap items-baseline gap-x-1 gap-y-1">
            <span>Within</span>
            <input
              type="number"
              min="1"
              value={conversionWindowResolved.value}
              onChange={(e) => {
                const raw = e.target.value;
                const value = parseInt(raw, 10);
                if (raw === "" || !Number.isFinite(value)) return;
                setConversionWindow({ value: Math.max(1, value) });
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!Number.isFinite(value) || value < 1) {
                  setConversionWindow({ value: 7 });
                }
              }}
              className="min-w-[2rem] max-w-[3.5rem] border-0 border-b border-dashed bg-transparent px-0 py-0 text-center text-[12px] font-semibold tabular-nums outline-none focus:border-solid"
              style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
              aria-label="Conversion window amount"
            />
            <span className="relative inline-flex items-center">
              <button
                type="button"
                aria-expanded={funnelMenuOpen === "unit"}
                aria-haspopup="listbox"
                aria-label="Conversion window unit"
                onClick={() => setFunnelMenuOpen((m) => (m === "unit" ? null : "unit"))}
                className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug hover:opacity-80"
                style={{ fontSize: "12px", color: "var(--text-primary)" }}
              >
                {WINDOW_UNIT_OPTIONS.find((u) => u.value === conversionWindowResolved.unit)?.label || "days"}
                <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
              </button>
              {funnelMenuOpen === "unit" && (
                <div
                  role="listbox"
                  className={`${pickerMenuClass} shadow-token-lg`}
                  style={{
                    backgroundColor: "var(--surface-base)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  {WINDOW_UNIT_OPTIONS.map((u) => {
                    const active = u.value === conversionWindowResolved.unit;
                    return (
                      <button
                        key={u.value}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          setConversionWindow({ unit: u.value });
                          setFunnelMenuOpen(null);
                        }}
                        className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                        style={{
                          color: active ? "var(--text-primary)" : "var(--text-secondary)",
                          backgroundColor: "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {u.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </span>
          </span>
          <span
            className="hidden sm:inline-block h-3 w-px shrink-0 self-center opacity-40"
            style={{ backgroundColor: "var(--border-default)" }}
            aria-hidden
          />
          <span className="inline-flex items-center gap-2">
            <span className="text-[11px] font-medium" style={{ color: "var(--text-primary)" }}>
              Any order
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={funnelAnyOrder}
              aria-label="Any order"
              onClick={(e) => {
                e.preventDefault();
                (onFunnelAnyOrderChange ?? (() => {}))(!funnelAnyOrder);
              }}
              className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 p-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--trodo-primary)] focus-visible:ring-offset-1"
              style={{ backgroundColor: funnelAnyOrder ? "var(--brand-primary)" : "var(--border-default)" }}
            >
              <span
                className="pointer-events-none absolute top-0.5 left-0.5 block h-4 w-4 rounded-full bg-surface shadow-sm ring-0 transition-[transform] duration-200 ease-out"
                style={{
                  transform: funnelAnyOrder ? "translateX(16px)" : "translateX(0)",
                }}
              />
            </button>
          </span>
        </p>
        {hasTransactionStep && funnelAnyOrder && (
          <p className="text-[10px] leading-snug" style={{ color: "var(--text-muted)" }}>
            Any order is not applied when a funnel includes a transaction step; the query uses sequential order with the same conversion window.
          </p>
        )}

        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted pt-2"
          style={{ borderColor: "var(--border-default)" }}
        >
          <div className="inline-flex items-center gap-1.5">
            <Hash className="h-3 w-3 shrink-0 text-ink" strokeWidth={2.25} aria-hidden />
            <div className="relative">
              <button
                type="button"
                aria-expanded={funnelMenuOpen === "subject"}
                aria-haspopup="listbox"
                aria-label="Funnel grain"
                onClick={() => setFunnelMenuOpen((m) => (m === "subject" ? null : "subject"))}
                className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
                style={{ fontSize: "11px" }}
              >
                {subjectDisplayLabel}
                <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
              </button>
              {funnelMenuOpen === "subject" && (
                <div
                  role="listbox"
                  className={`${pickerMenuClass} shadow-token-lg`}
                  style={{
                    backgroundColor: "var(--surface-base)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  {countSubjects.map((s) => {
                    const active = isSubjectRowActive(s);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        title={s.label}
                        onClick={() => handleCountSubjectPick(s)}
                        className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                        style={{
                          color: active ? "var(--text-primary)" : "var(--text-secondary)",
                          backgroundColor: "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <button
              type="button"
              aria-expanded={funnelMenuOpen === "measuring"}
              aria-haspopup="listbox"
              aria-label="Measuring"
              onClick={() => setFunnelMenuOpen((m) => (m === "measuring" ? null : "measuring"))}
              className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
              style={{ fontSize: "11px" }}
            >
              {measuringLabel}
              <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
            </button>
            {funnelMenuOpen === "measuring" && (
              <div
                role="listbox"
                className={`${pickerMenuClass} shadow-token-lg`}
                style={{
                  backgroundColor: "var(--surface-base)",
                  borderColor: "var(--border-default)",
                }}
              >
                {MEASURING_OPTIONS.map((opt) => {
                  const active = opt.value === measuringResolved;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        if (onFunnelMeasuringChange) {
                          onFunnelMeasuringChange(opt.value);
                          onFunnelMeasuringPropertyChange?.(null);
                          if (opt.value !== "time_to_convert") {
                            onFunnelMeasuringAggregationChange?.("average");
                          }
                        }
                        setFunnelMenuOpen(null);
                      }}
                      className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                      style={{
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {measuringResolved === "time_to_convert" && (
            <>
              <div className="relative">
                <button
                  type="button"
                  aria-expanded={funnelMenuOpen === "aggregation"}
                  aria-haspopup="listbox"
                  aria-label="Time aggregation"
                  onClick={() => setFunnelMenuOpen((m) => (m === "aggregation" ? null : "aggregation"))}
                  className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
                  style={{ fontSize: "11px" }}
                >
                  {ttcAggLabel}
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
                </button>
                {funnelMenuOpen === "aggregation" && (
                  <div
                    role="listbox"
                    className={`${pickerMenuClass} shadow-token-lg`}
                    style={{
                      backgroundColor: "var(--surface-base)",
                      borderColor: "var(--border-default)",
                    }}
                  >
                    {TTC_AGG_OPTIONS.map((opt) => {
                      const active = opt.value === (funnelMeasuringAggregation || "average");
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => {
                            if (onFunnelMeasuringAggregationChange) {
                              onFunnelMeasuringAggregationChange(opt.value);
                              if (opt.value !== "percentile") {
                                onFunnelMeasuringPercentileValueChange?.(50);
                              }
                            }
                            setFunnelMenuOpen(null);
                          }}
                          className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                          style={{
                            color: active ? "var(--text-primary)" : "var(--text-secondary)",
                            backgroundColor: "transparent",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {funnelMeasuringAggregation === "percentile" && (
                <label className="inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                  <span>P</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={funnelMeasuringPercentileValue || 50}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      onFunnelMeasuringPercentileValueChange?.(
                        Number.isNaN(value) ? 50 : Math.max(0, Math.min(100, value))
                      );
                    }}
                    className="w-12 border-0 border-b border-dashed bg-transparent px-0 py-0 text-center text-[11px] font-semibold tabular-nums outline-none"
                    style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
                  />
                </label>
              )}
            </>
          )}
        </div>
      </div>

      {/* Event Property Filter Module: for event-kind steps only */}
      {isPropertyFilterOpen && selectedStepForFilter && (selectedStepForFilter.entityKind === 'event' || !selectedStepForFilter.entityKind) && (
        <EventPropertyFilterModule
          isOpen={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedStepForFilter(null);
            setSelectedStepIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`step-${selectedStepIndex}`]
          }
          eventName={selectedStepForFilter.eventName}
          eventId={selectedStepForFilter.id}
          teamId={teamId}
          filters={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []) : []}
          onFiltersChange={handleFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}

      {/* Agent / Span step: promoted PropertyPickerPopover with the
          matching property catalog (run / span / user) for the step's
          parent agent. */}
      {isPropertyFilterOpen && selectedStepForFilter && (selectedStepForFilter.entityKind === 'agent' || selectedStepForFilter.entityKind === 'span') && (
        <PropertyPickerPopover
          open={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedStepForFilter(null);
            setSelectedStepIndex(null);
            setEditingPropertyFilter(null);
          }}
          anchorRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`step-${selectedStepIndex}`]
          }
          teamId={teamId}
          scope="agent"
          agentName={selectedStepForFilter.entityKind === 'agent' ? selectedStepForFilter.eventName : null}
          onPick={(picked) => {
            if (selectedStepIndex == null) return;
            const stepKind = selectedStepForFilter.entityKind;
            // Strip the picker's `span.` / `attributes.` prefixes so the
            // backend compiler routes the filter through the right entity
            // property accessor. The compiler handles the bare path.
            const rawField = picked.field || '';
            const normalisedField = rawField
              .replace(/^span\./, '')
              .replace(/^attributes\./, 'attributes.');
            const op = defaultOperatorForType(picked.dataType);
            const isString = (picked.dataType || 'string') === 'string';
            const newFilter = {
              id: `filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              field: normalisedField,
              property: normalisedField,
              propertyType: picked.dataType || 'string',
              propertySource: stepKind, // 'agent' | 'span'
              operator: op,
              value: isString && (op === 'is' || op === 'is_not') ? [] : '',
              propertyLabel: rawField,
              category: stepKind === 'span' ? 'span_properties' : 'agent_properties',
              connector: (funnelSteps[selectedStepIndex]?.filters || []).length === 0 ? 'WHERE' : 'AND',
              sampleValues: picked.sampleValues || [],
            };
            const currentFilters = funnelSteps[selectedStepIndex]?.filters || [];
            if (editingPropertyFilter) {
              handleUpdateStep(selectedStepIndex, {
                filters: currentFilters.map((f) => f.id === editingPropertyFilter.id ? { ...newFilter, id: editingPropertyFilter.id } : f),
              });
              setEditingPropertyFilter(null);
            } else {
              handleUpdateStep(selectedStepIndex, { filters: [...currentFilters, newFilter] });
            }
            setIsPropertyFilterOpen(false);
            setSelectedStepForFilter(null);
            setSelectedStepIndex(null);
          }}
        />
      )}
    </>
  );
}
